import { FC, useState } from "react";
import {
  Dialog,
  DialogContent,
} from "@shadcn/components/ui/dialog";
import { Button } from "@shadcn/components/ui/button";
import { Clock, Headset, Loader2, PartyPopper } from "lucide-react";
import { DialpadOpeningHours } from "@/data/dialpad/api";

type Props = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => Promise<{ success: boolean; position: number }>;
  isClosed?: boolean;
  availabilityError?: boolean;
  openingHours?: DialpadOpeningHours;
};

const DAY_LABELS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"] as const;

function getTimezoneDisplayName(timezone: string): string {
  if (!timezone) return "";
  try {
    const formatter = new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      timeZoneName: "long",
    });
    const parts = formatter.formatToParts(new Date());
    const tzPart = parts.find((p) => p.type === "timeZoneName");
    return tzPart?.value ?? timezone;
  } catch {
    return timezone;
  }
}

function formatHourSlots(hours: string[]): string {
  if (!hours || hours.length === 0) return "Closed";
  const slots: string[] = [];
  for (let i = 0; i < hours.length; i += 2) {
    const open = hours[i];
    const close = hours[i + 1];
    if (open && close) slots.push(`${open} – ${close}`);
  }
  return slots.length > 0 ? slots.join(", ") : "Closed";
}

function getDayHours(hours: DialpadOpeningHours, dayIndex: number): string[] {
  const map = [
    hours.mondayHours,
    hours.tuesdayHours,
    hours.wednesdayHours,
    hours.thursdayHours,
    hours.fridayHours,
    hours.saturdayHours,
    hours.sundayHours,
  ];
  return map[dayIndex] ?? [];
}

export const CallbackRequestedModal: FC<Props> = ({
  open,
  onClose,
  onConfirm,
  isClosed,
  availabilityError,
  openingHours,
}) => {
  const [confirming, setConfirming] = useState(false);
  const [position, setPosition] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const [requestFailed, setRequestFailed] = useState(false);

  const handleConfirm = async () => {
    setConfirming(true);
    setRequestFailed(false);
    try {
      const result = await onConfirm();
      setPosition(result.position);
      setConfirmed(true);
      setRequestFailed(!result.success);
    } finally {
      setConfirming(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setConfirmed(false);
      setPosition(0);
      setRequestFailed(false);
    }, 200);
  };

  const renderClosedContent = () => (
    <>
      <div className="bg-muted/50 flex items-center justify-center py-6">
        <div className="rounded-full bg-muted p-5">
          <Clock className="h-10 w-10 text-muted-foreground" />
        </div>
      </div>
      <div className="px-8 pb-8 pt-4 text-center">
        <h2 className="text-2xl font-bold tracking-tight">
          {availabilityError ? "Unable to check availability" : "We're currently closed"}
        </h2>
        <p className="mt-3 text-base text-muted-foreground leading-relaxed">
          {availabilityError
            ? "We couldn't check if our call centre is open. Please try again later."
            : "Our call centre is closed right now, but we'd love to hear from you during our opening hours."}
        </p>
        {openingHours?.hoursEnabled && (
          <div className="mt-6 rounded-lg border bg-card p-4 text-left">
            <h3 className="text-sm font-semibold mb-3">
              Opening hours
              {openingHours.timezone
                ? ` (${getTimezoneDisplayName(openingHours.timezone) || openingHours.timezone})`
                : ""}
            </h3>
            <div className="space-y-1.5">
              {DAY_LABELS.map((day, i) => {
                const slots = getDayHours(openingHours, i);
                const display = formatHourSlots(slots);
                const closed = display === "Closed";
                return (
                  <div key={day} className="flex justify-between text-sm">
                    <span className="font-medium">{day}</span>
                    <span className={closed ? "text-muted-foreground" : ""}>{display}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div className="mt-8 flex justify-center">
          <Button size="lg" variant="outline" onClick={handleClose} className="min-w-[120px]">
            Ok
          </Button>
        </div>
      </div>
    </>
  );

  const renderConfirmContent = () => (
    <>
      <div className="bg-primary/10 flex items-center justify-center py-6">
        <div className="rounded-full bg-primary/15 p-5 pb-0">
          <Headset className="h-10 w-10 text-primary" />
        </div>
      </div>
      <div className="px-8 pb-8 pt-4 text-center">
        <h2 className="text-2xl font-bold tracking-tight">
          Request a callback?
        </h2>
        <p className="mt-3 text-base text-muted-foreground leading-relaxed">
          If you confirm, we&apos;ll call you back as soon as someone is available.
        </p>
        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button
            variant="outline"
            size="lg"
            onClick={handleClose}
            disabled={confirming}
            className="sm:min-w-[120px]"
          >
            Cancel
          </Button>
          <Button
            size="lg"
            onClick={handleConfirm}
            disabled={confirming}
            className="sm:min-w-[120px]"
          >
            {confirming && <Loader2 className="h-4 w-4 animate-spin" />}
            Confirm
          </Button>
        </div>
      </div>
    </>
  );

  const renderSuccessContent = () => (
    <>
      <div
        className={`flex items-center justify-center py-6 ${
          requestFailed ? "bg-destructive/10" : "bg-green-50"
        }`}
      >
        <div
          className={`rounded-full p-5 ${
            requestFailed ? "bg-destructive/20" : "bg-green-100"
          }`}
        >
          {requestFailed ? (
            <Clock className="h-10 w-10 text-destructive" />
          ) : (
            <PartyPopper className="h-10 w-10 text-green-600" />
          )}
        </div>
      </div>
      <div className="px-8 pb-8 pt-4 text-center">
        <h2 className="text-2xl font-bold tracking-tight">
          {requestFailed ? "Request unsuccessful" : "Thanks!"}
        </h2>
        <p className="mt-3 text-base text-muted-foreground leading-relaxed">
          {requestFailed
            ? "We couldn't process your callback request. Please try again later."
            : position > 0
              ? `You're in position ${position}, someone will call you shortly.`
              : "Someone will call you shortly."}
        </p>
        <div className="mt-8 flex justify-center">
          <Button size="lg" onClick={handleClose} className="min-w-[120px]">
            Ok
          </Button>
        </div>
      </div>
    </>
  );

  const getContent = () => {
    if (isClosed || availabilityError) return renderClosedContent();
    if (confirmed) return renderSuccessContent();
    return renderConfirmContent();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        {getContent()}
      </DialogContent>
    </Dialog>
  );
};
